import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getStudentMonthlyLearningData } from "../../../network/Endpoint";
import StudentMonthlyReport from "../components/StudentMonthlyReport";
import SideMenu from "../../../components/SideMenu";

const StudentMonthlyReportSharePage = () => {
  const [searchParams] = useSearchParams();
  
  // 데이터 로딩 상태
  const [loading, setLoading] = useState(true);
  const [showLoadingUI, setShowLoadingUI] = useState(false);
  const loadingTimerRef = useRef(null);
  
  // 상태 관리
  const [selectedMonth, setSelectedMonth] = useState({ year: null, month: null });
  const [studentId, setStudentId] = useState(null);
  const [studentInfo, setStudentInfo] = useState(null);
  
  // 월간 학습 데이터 상태
  const [monthlyData, setMonthlyData] = useState(null);
  const [error, setError] = useState(null);
  const [hasValidParams, setHasValidParams] = useState(false);
  
  // 모바일 감지
  const [isMobile, setIsMobile] = useState(false);
  
  // 보고서 실제 높이 측정
  const [reportHeight, setReportHeight] = useState(0);
  const reportRef = useRef(null);

  // 모바일 감지 및 뷰포트 설정
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // 모바일에서 뷰포트 설정 최적화
    if (window.innerWidth <= 768) {
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
      }
    }
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      // 컴포넌트 언마운트 시 뷰포트 설정 복원
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1');
      }
    };
  }, []);

  // 로딩 상태 변경 시 지연된 로딩 UI 표시 처리
  useEffect(() => {
    if (loading) {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      
      loadingTimerRef.current = setTimeout(() => {
        if (loading) {
          setShowLoadingUI(true);
        }
      }, 300);
    } else {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setShowLoadingUI(false);
    }
    
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [loading]);

  // 보안 토큰 디코딩 함수
  const decodeSecureToken = (token) => {
    try {
      console.log('디코딩 시작 - 토큰:', token);
      
      // 토큰을 마지막 '-'로 분리해서 hash와 timestamp 구분
      const lastDashIndex = token.lastIndexOf('-');
      if (lastDashIndex === -1) {
        console.error('토큰 형식 오류: timestamp 구분자 없음');
        return null;
      }
      
      const hash = token.substring(0, lastDashIndex);
      const timestamp = token.substring(lastDashIndex + 1);
      
      console.log('Hash:', hash);
      console.log('Timestamp:', timestamp);
      
      if (!hash || !timestamp) {
        console.error('Hash 또는 timestamp 누락');
        return null;
      }
      
      const secret = process.env.REACT_APP_REPORT_SECRET || 'arkmath_secret_2024';
      console.log('사용된 secret:', secret);
      
      // Base64 디코딩
      let decodedData;
      try {
        // 패딩 추가 (Base64 길이가 4의 배수가 되도록)
        const paddedHash = hash + '==='.slice(0, (4 - hash.length % 4) % 4);
        decodedData = atob(paddedHash);
        console.log('디코딩된 데이터:', decodedData);
      } catch (decodeError) {
        console.error('Base64 디코딩 실패:', decodeError);
        return null;
      }
      
      // secret이 포함되어 있는지 확인
      if (decodedData.includes(secret)) {
        console.log('Secret 검증 성공');
        
        // 원본 데이터 구조: studentId-year-month-timestamp-secret
        // timestamp와 secret을 제거하고 학생정보 추출
        const secretSuffix = `-${timestamp}-${secret}`;
        const dataWithoutSecret = decodedData.replace(secretSuffix, '');
        console.log('Secret 제거 후 데이터:', dataWithoutSecret);
        
        const [studentId, year, month] = dataWithoutSecret.split('-');
        console.log('파싱된 데이터:', { studentId, year, month });
        
        if (studentId && year && month) {
          const result = {
            studentId: studentId,
            year: parseInt(year),
            month: parseInt(month)
          };
          console.log('최종 결과:', result);
          return result;
        } else {
          console.error('학생 정보 파싱 실패');
        }
      } else {
        console.error('Secret 검증 실패 - decodedData에 secret이 포함되지 않음');
      }
      
      return null;
    } catch (error) {
      console.error('토큰 디코딩 실패:', error);
      return null;
    }
  };

  // URL 파라미터에서 데이터 추출
  useEffect(() => {
    const token = searchParams.get('token');
    
    console.log('URL 토큰:', token);
    
    if (token) {
      const decodedData = decodeSecureToken(token);
      
      if (decodedData) {
        setSelectedMonth({
          year: decodedData.year,
          month: decodedData.month
        });
        setStudentId(decodedData.studentId);
        setHasValidParams(true);
        setError(null);
        console.log('토큰 디코딩 성공:', decodedData);
      } else {
        setError('유효하지 않은 링크입니다.');
        setHasValidParams(false);
        setLoading(false);
      }
    } else {
      setError('잘못된 링크입니다. 토큰이 누락되었습니다.');
      setHasValidParams(false);
      setLoading(false);
    }
  }, [searchParams]);

  // 데이터 로딩
  useEffect(() => {
    if (studentId && selectedMonth.year && selectedMonth.month) {
      fetchStudentMonthlyResults();
    }
  }, [studentId, selectedMonth]);

  // 보고서 높이 측정
  useEffect(() => {
    if (reportRef.current && !loading && monthlyData) {
      const measureHeight = () => {
        const height = reportRef.current.scrollHeight;
        setReportHeight(height);
        console.log('보고서 실제 높이:', height);
      };
      
      // 렌더링 완료 후 높이 측정
      setTimeout(measureHeight, 100);
      
      // 윈도우 리사이즈 시에도 다시 측정
      window.addEventListener('resize', measureHeight);
      return () => window.removeEventListener('resize', measureHeight);
    }
  }, [loading, monthlyData, isMobile]);

  // API 호출하여 학생 월간 결과 데이터 가져오기
  const fetchStudentMonthlyResults = async () => {
    if (!studentId || !selectedMonth.year || !selectedMonth.month) return;
    
    setLoading(true);
    setError(null);

    try {
      const body = {
        studentId: studentId,
        params: {
          year: selectedMonth.year,
          month: selectedMonth.month
        }
      };
      
      console.log('API 호출 시작:', body);
      const res = await getStudentMonthlyLearningData(body);
      console.log('API 응답:', res.data);
      
      if (res.data && res.data.status.code === "Success") {
        const responseData = res.data.data;
        setMonthlyData(responseData);
        
        // API 응답에서 학생 정보 설정
        if (responseData.studentName) {
          setStudentInfo({
            studentId: studentId,
            name: responseData.studentName
          });
        } else {
          // studentName이 없으면 임시로 설정
          setStudentInfo({
            studentId: studentId,
            name: `학생 ${studentId}`
          });
        }
        
        console.log("월간 데이터 로딩 완료:", responseData);
      } else {
        console.error('API 응답 형식이 올바르지 않습니다:', res.data);
        setError(`데이터를 불러올 수 없습니다: ${res.data?.status?.message || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('학생 월간 결과를 가져오는 중 오류 발생:', error);
      setError(`API 호출 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 모바일 스케일 계산
  const getMobileScale = () => {
    if (!isMobile) return 1;
    const viewportWidth = window.innerWidth;
    const reportWidth = 1040;
    const padding = 20; // 좌우 여백
    return Math.min((viewportWidth - padding) / reportWidth, 1);
  };

  return (
      <>
        <div
          className={`${isMobile ? 'min-h-0' : 'min-h-screen'} bg-gray-50 ${isMobile ? 'overflow-x-hidden' : ''}`}
          style={isMobile ? {
            touchAction: 'pan-y',
            WebkitOverflowScrolling: 'touch',
            minHeight: 'auto' // 모바일에서는 최소 높이를 자동으로 설정
          } : {}}
        >
          {/* 로딩 UI */}
          {showLoadingUI && (
            <div className="fixed inset-0 bg-white bg-opacity-70 flex justify-center items-center z-50">
              <div className="flex flex-col items-center bg-white p-6 rounded-lg shadow-md">
                <div className="w-12 h-12 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mb-4"></div>
                <p className="text-gray-700">월간 학습 데이터를 불러오는 중...</p>
              </div>
            </div>
          )}

          {/* 월간 결과 표시 */}
          {!loading && monthlyData && studentInfo && (
            <div
              className={`flex justify-center ${isMobile ? 'px-2 py-2' : ''}`}
              style={isMobile && reportHeight > 0 ? {
                maxHeight: `${reportHeight * getMobileScale() + 50}px`, // 보고서 실제 높이에 스케일 적용 + 여유 공간
                overflow: 'visible'
              } : {}}
            >
              <div
                ref={reportRef}
                className="w-[1040px]"
                style={isMobile ? {
                  transform: `scale(${getMobileScale()})`,
                  transformOrigin: 'top center',
                  marginBottom: `${(1 - getMobileScale()) * 100}px`, // 마진을 더 줄임
                  touchAction: 'pan-y',
                  width: '1040px', // 너비 고정 유지
                  overflow: 'visible' // 내용이 잘리지 않도록 설정
                } : {}}
              >
                <StudentMonthlyReport
                  monthlyData={monthlyData}
                  selectedMonth={selectedMonth}
                  studentInfo={studentInfo}
                />
              </div>
            </div>
          )}

          {/* 에러 또는 데이터가 없는 경우 */}
          {!loading && error && (
            <div className="flex justify-center items-center min-h-screen">
              <div className="text-center max-w-md">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">보고서를 찾을 수 없습니다</h2>
                <p className="text-gray-600 mb-4">{error}</p>
                <div className="text-sm text-gray-500">
                  <p>현재 파라미터:</p>
                  <p>학생ID: {studentId || '없음'}</p>
                  <p>년도: {selectedMonth.year || '없음'}</p>
                  <p>월: {selectedMonth.month || '없음'}</p>
                </div>
              </div>
            </div>
          )}

          {/* 파라미터는 있지만 데이터가 없는 경우 */}
          {!loading && !error && hasValidParams && (!monthlyData || !studentInfo) && (
            <div className="flex justify-center items-center min-h-screen">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">데이터를 불러오는 중 문제가 발생했습니다</h2>
                <p className="text-gray-600">잠시 후 다시 시도해주세요.</p>
              </div>
            </div>
          )}
        </div>
        <SideMenu></SideMenu>
      </>
  );
};

export default StudentMonthlyReportSharePage; 